import {autoUpdate, flip, Placement, shift, useFloating, useMergeRefs} from '@floating-ui/react';

import {forwardRef, HTMLAttributes, ReactNode} from 'react';

import {Nullish} from 'shared/web';

type PopperAnchor = HTMLElement | (() => HTMLElement | null) | Nullish;

type PopperModifiers = {
  flip?: {enabled?: boolean};
  preventOverflow?: {enabled?: boolean; boundariesElement?: 'viewport'};
};

type PopperChildProps = {placement: Placement};

type PopperOwnProps = {
  anchorEl?: PopperAnchor;
  open?: boolean;
  placement?: Placement;
  modifiers?: PopperModifiers;
  disablePortal?: boolean;
  children?: ReactNode | ((childProps: PopperChildProps) => ReactNode);
  'data-testid'?: string;
};

type PopperProps = PopperOwnProps & Omit<HTMLAttributes<HTMLDivElement>, keyof PopperOwnProps>;

const getAnchorEl = (anchorEl: PopperAnchor) => {
  const resolved = typeof anchorEl === 'function' ? anchorEl() : anchorEl;
  return resolved ?? null;
};

const buildMiddleware = (modifiers: PopperModifiers | undefined) => {
  const middleware = [];

  // crossAxis off: flip swaps sides only (e.g. right -> left), never start/end alignment.
  if (modifiers?.flip?.enabled) {
    middleware.push(flip({crossAxis: false}));
  }

  // crossAxis lets shift pull the menu back on-screen when it overflows the placement side.
  if (modifiers?.preventOverflow?.enabled) {
    middleware.push(shift({crossAxis: true}));
  }

  return middleware;
};

export const Popper = forwardRef<HTMLDivElement, PopperProps>((props, ref) => {
  const {
    anchorEl,
    children,
    modifiers,
    open = false,
    placement = 'bottom',
    disablePortal: _disablePortal,
    ...other
  } = props;

  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
  } = useFloating({
    open,
    placement,
    strategy: 'fixed',
    elements: {reference: getAnchorEl(anchorEl)},
    middleware: buildMiddleware(modifiers),
    whileElementsMounted: autoUpdate,
  });

  // useMergeRefs keys on the array elements, so an inline array with stable refs is fine.
  const floatingRef = useMergeRefs([refs.setFloating, ref]);

  if (!open) {
    return null;
  }

  return (
    <div ref={floatingRef} role="tooltip" {...other} style={floatingStyles}>
      {typeof children === 'function' ? children({placement: resolvedPlacement}) : children}
    </div>
  );
});

Popper.displayName = 'Popper';
